import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clientSourceSchema, clientStatusSchema,
  type ClientOutput, type CreateClientInput, type UpdateClientInput,
} from '@1financial/shared';
import { COUNTRIES, JURISDICTIONS } from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch, captureAuthIdentity } from '../lib/auth-context.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { useEscape } from '../hooks/useEscape.js';
import { SearchableSelect } from './SearchableSelect.js';
import { TagCheckboxes, syncClientTags } from './TagCheckboxes.js';
import { CompaniesHouseReader } from './CompaniesHouseReader.js';
import { DateInput } from './DateInput.js';

/** OPS 3.5 #9: ONE client form for create AND edit — the card can never drift.
 *  Create mode: the modal POSTs itself (then assigns tags) and reports the new
 *  id via onCreated. Edit mode: the modal syncs tags then hands the patch to
 *  the parent's mutation via onSubmit. Company is OPTIONAL (OPS 3.5 #2) —
 *  fresh registrations have no company name yet; the API schema already
 *  accepted null. */
export function ClientFormModal({ mode, client, onClose, onCreated, onSubmit, error, submitting }: {
  mode: 'create' | 'edit';
  /** Required in edit mode. */
  client?: ClientOutput;
  onClose: () => void;
  /** create mode */
  onCreated?: (id: string) => void;
  /** edit mode */
  onSubmit?: (patch: UpdateClientInput) => void;
  error?: string | null;
  submitting?: boolean;
}) {
  const queryClient = useQueryClient();
  const { canDo } = useCapabilities();
  const canTags = canDo('tags.manage');

  const [form, setForm] = useState<CreateClientInput>(() => mode === 'edit' && client ? {
    name: client.name,
    company: client.company,
    companyNumber: client.companyNumber ?? null,
    email: client.email,
    phone: client.phone,
    country: client.country,
    industry: client.industry,
    website: client.website,
    address: client.address,
    source: client.source,
    status: client.status,
    ownerId: client.ownerId,
    notesMarkdown: client.notesMarkdown,
    legalName: client.legalName,
    tradeLicenseNo: client.tradeLicenseNo,
    trn: client.trn,
    incorporationDate: client.incorporationDate,
    jurisdiction: client.jurisdiction,
    fyEnd: client.fyEnd,
  } : {
    name: '',
    company: null,
    email: null,
    phone: null,
    country: null,
    industry: null,
    website: null,
    address: null,
    source: null,
    status: 'LEAD',
    ownerId: null,
    notesMarkdown: null,
    legalName: null,
    tradeLicenseNo: null,
    trn: null,
    incorporationDate: null,
    jurisdiction: null,
    fyEnd: null,
  });

  const [localError, setLocalError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Tags — create: assigned after the POST; edit: diffed against the server set.
  const [createTagIds, setCreateTagIds] = useState<Set<string>>(new Set());
  const assignedTags = useQuery({
    queryKey: ['entity-tags', 'CLIENT', client?.id],
    enabled: mode === 'edit' && canTags && !!client,
    queryFn: async () => {
      const res = await fetch(`/v1/tags/for/CLIENT/${client!.id}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: Array<{ id: string }> } };
      return body.data.items.map((t) => t.id);
    },
  });
  const [editTagIds, setEditTagIds] = useState<Set<string> | null>(null);
  const effectiveEditTagIds = editTagIds ?? new Set(assignedTags.data ?? []);

  function update<K extends keyof CreateClientInput>(k: K, v: CreateClientInput[K]) {
    setForm((f) => ({ ...f, [k]: v || null }));
  }

  const inputCls = 'w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const isCurrent = captureAuthIdentity();
    const missing: string[] = [];
    if (!form.name.trim()) missing.push('Name');
    if (!form.country) missing.push('Country');
    if (!form.industry?.trim()) missing.push('Industry');
    if (missing.length > 0) {
      setLocalError(`Required fields missing: ${missing.join(', ')}.`);
      return;
    }
    setLocalError(null);

    if (mode === 'create') {
      setBusy(true);
      try {
        const res = await fetch('/v1/clients', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken() ?? ''}`,
          },
          body: JSON.stringify(form),
        });
        const body = await res.json() as { ok?: boolean; data?: { id: string }; error?: { message?: string } };
        if (!res.ok || !body.ok || !body.data) {
          setLocalError(body.error?.message ?? `Error ${res.status}: ${res.statusText}`);
          return;
        }
        if (!isCurrent()) return;
        if (canTags && createTagIds.size > 0) {
          try {
            await syncClientTags(body.data.id, new Set(), createTagIds);
            if (!isCurrent()) return;
            queryClient.invalidateQueries({ queryKey: ['entity-tags'] });
          } catch (tagErr) {
            if (!isCurrent()) return;
            console.error('[clients] tag assignment after create failed:', tagErr);
          }
        }
        if (isCurrent()) onCreated?.(body.data.id);
      } catch (err) {
        if (isCurrent()) setLocalError((err as Error).message);
      } finally {
        if (isCurrent()) setBusy(false);
      }
      return;
    }

    // edit
    if (!client || !isCurrent()) return;
    if (canTags && editTagIds !== null && assignedTags.data) {
      setBusy(true);
      try {
        await syncClientTags(client.id, new Set(assignedTags.data), editTagIds);
        if (!isCurrent()) return;
        queryClient.invalidateQueries({ queryKey: ['entity-tags'] });
      } catch (tagErr) {
        if (!isCurrent()) return;
        setBusy(false);
        setLocalError(tagErr instanceof Error ? `Tags: ${tagErr.message}` : 'Failed to update tags');
        return;
      }
      setBusy(false);
    }
    if (isCurrent()) onSubmit?.(form as UpdateClientInput);
  }

  const working = busy || !!submitting;

  return (
    <ModalShell title={mode === 'create' ? 'New client' : 'Edit client'} onClose={onClose}>
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3">
        {mode === 'edit' && <CompaniesHouseReader mode={mode} clientId={client?.id} form={form} setForm={setForm} />}
        <Field label="UK company number">
          <input aria-label="UK company number" value={form.companyNumber ?? ''} onChange={(e) => update('companyNumber', e.target.value.toUpperCase())} maxLength={8} pattern="[A-Z0-9]{8}" className={inputCls} />
          <p className="text-xs text-muted mt-1">Optional — eight characters, including prefixes and leading zeros.</p>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name" required>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Full name or company"
              className={inputCls}
              required
            />
            <p className="text-xs text-muted mt-1">Primary contact name or company name.</p>
          </Field>
          <Field label="Company">
            <input
              value={form.company ?? ''}
              onChange={(e) => update('company', e.target.value)}
              placeholder="Legal entity name"
              className={inputCls}
            />
            <p className="text-xs text-muted mt-1">Optional — fresh registrations have no company name yet.</p>
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Email">
            <input
              type="email"
              value={form.email ?? ''}
              onChange={(e) => update('email', e.target.value)}
              placeholder="contact@company.com"
              className={inputCls}
            />
            <p className="text-xs text-muted mt-1">Main contact email.</p>
          </Field>
          <Field label="Phone">
            <input
              value={form.phone ?? ''}
              onChange={(e) => update('phone', e.target.value)}
              placeholder="+971 50 123 4567"
              className={inputCls}
            />
            <p className="text-xs text-muted mt-1">Include the country code, e.g. +971 …</p>
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Country" required>
            <SearchableSelect
              value={form.country ?? ''}
              onChange={(v) => update('country', v)}
              placeholder="— select —"
              required
              options={COUNTRIES.map((c) => ({ value: c.code, label: c.label }))}
            />
            <p className="text-xs text-muted mt-1">Where the client is based.</p>
          </Field>
          <Field label="Industry" required>
            <input
              value={form.industry ?? ''}
              onChange={(e) => update('industry', e.target.value)}
              placeholder="e.g. Financial Services"
              className={inputCls}
              required
            />
            <p className="text-xs text-muted mt-1">Client's line of business.</p>
          </Field>
        </div>

        <Field label="Website">
          <input
            value={form.website ?? ''}
            onChange={(e) => update('website', e.target.value)}
            placeholder="https://company.com"
            className={inputCls}
          />
          <p className="text-xs text-muted mt-1">Full URL including https:// — use a profile URL if no site.</p>
        </Field>

        <Field label="Address">
          <textarea
            value={form.address ?? ''}
            onChange={(e) => update('address', e.target.value)}
            placeholder="Office address"
            rows={2}
            className={`${inputCls} resize-none`}
          />
          <p className="text-xs text-muted mt-1">Registered or office address.</p>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Legal name">
            <input
              value={form.legalName ?? ''}
              onChange={(e) => update('legalName', e.target.value)}
              placeholder="Exact registered name"
              className={inputCls}
            />
            <p className="text-xs text-muted mt-1">Optional — can be filled later via document review.</p>
          </Field>
          <Field label="Jurisdiction">
            <select
              value={form.jurisdiction ?? ''}
              onChange={(e) => update('jurisdiction', (e.target.value || null) as CreateClientInput['jurisdiction'])}
              className={inputCls}
            >
              <option value="">—</option>
              {JURISDICTIONS.map((j) => (
                <option key={j.code} value={j.code}>{j.label}</option>
              ))}
            </select>
            <p className="text-xs text-muted mt-1">Operating jurisdiction of the entity.</p>
          </Field>
          <Field label="Trade license no.">
            <input
              value={form.tradeLicenseNo ?? ''}
              onChange={(e) => update('tradeLicenseNo', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="TRN">
            <input
              value={form.trn ?? ''}
              onChange={(e) => update('trn', e.target.value)}
              placeholder="15-digit tax registration number"
              className={inputCls}
            />
          </Field>
          <Field label="Incorporation date">
            <DateInput
              value={form.incorporationDate ? form.incorporationDate.slice(0, 10) : ''}
              onChange={(e) => update('incorporationDate', e.target.value ? `${e.target.value}T00:00:00.000Z` : null)}
              className={inputCls}
            />
          </Field>
          <Field label="Financial year end">
            <DateInput
              value={form.fyEnd ? form.fyEnd.slice(0, 10) : ''}
              onChange={(e) => update('fyEnd', e.target.value ? `${e.target.value}T00:00:00.000Z` : null)}
              className={inputCls}
            />
            <p className="text-xs text-muted mt-1">Client reference only — tax deadlines require reviewed actual period evidence.</p>
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Source">
            <select
              value={form.source ?? ''}
              onChange={(e) => update('source', (e.target.value as typeof clientSourceSchema.options[number]) || null)}
              className={inputCls}
            >
              <option value="">—</option>
              {clientSourceSchema.options.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <p className="text-xs text-muted mt-1">How this client found us.</p>
          </Field>
          <Field label="Owner ID">
            <input
              value={form.ownerId ?? ''}
              onChange={(e) => update('ownerId', e.target.value || null)}
              placeholder="Optional user ID"
              className={inputCls}
            />
            <p className="text-xs text-muted mt-1">Optional — internal user ID of the record owner.</p>
          </Field>
        </div>

        <Field label="Status" required>
          <select
            value={form.status ?? 'LEAD'}
            onChange={(e) => update('status', e.target.value as ClientOutput['status'])}
            className={inputCls}
            required
          >
            {clientStatusSchema.options.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <p className="text-xs text-muted mt-1">{mode === 'create' ? 'Defaults to LEAD for new clients.' : 'Current lifecycle stage of the client.'}</p>
        </Field>

        <Field label="Notes">
          <textarea
            value={form.notesMarkdown ?? ''}
            onChange={(e) => update('notesMarkdown', e.target.value)}
            placeholder="Internal notes (markdown supported)"
            rows={3}
            className={`${inputCls} resize-none`}
          />
          <p className="text-xs text-muted mt-1">Internal context on the client (markdown supported).</p>
        </Field>

        {canTags && (
          <Field label="Tags">
            {mode === 'create'
              ? <TagCheckboxes selected={createTagIds} onChange={setCreateTagIds} />
              : <TagCheckboxes selected={effectiveEditTagIds} onChange={setEditTagIds} />}
            <p className="text-xs text-muted mt-1">{mode === 'create' ? 'Tags are assigned when the client is created.' : 'Changes apply when you save.'}</p>
          </Field>
        )}

        {(localError ?? error) && (
          <p className="text-xs text-rose-400 bg-rose-950/30 border border-rose-800 rounded-lg px-3 py-2">{localError ?? error}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button
            type="submit"
            disabled={working}
            className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {working ? (mode === 'create' ? 'Creating…' : 'Saving…') : (mode === 'create' ? 'Create client' : 'Save changes')}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEscape(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col mx-4 shadow-2xl animate-pop">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-muted hover:text-white text-sm">✕</button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-muted mb-1">{label}{required && <span className="text-rose-400"> *</span>}</label>
      {children}
    </div>
  );
}
